/**
 * Moving between add-in pages while staying inside the launcher.
 *
 * Each page of this add-in is its own Geotab add-in with its own HTML entry point, so there is no
 * router to push a route onto. Two separate things are needed to jump from one page to another
 * with a vehicle in hand: an event telling the launcher which page to show, and somewhere to
 * leave the vehicle id for the page that is about to mount. A query parameter would not do, since
 * the launcher never reloads the document.
 */

const NAVIGATE_ADDIN_EVENT = "fairfleet:navigate-addin";
/**
 * Which page a handed-over vehicle is meant for.
 *
 * Keyed per page rather than one shared slot: a reader can jump to the motor data, come back and
 * jump to the route, and a single slot would have been emptied by the first page to mount.
 */
export type HandoverPage = "motorData" | "route";

const DEVICE_STORAGE_KEYS: Record<HandoverPage, string> = {
  motorData: "fairfleet:pending-motor-data-device-id",
  route: "fairfleet:pending-route-device-id",
};

export interface NavigateAddInDetail {
  /** The add-in HTML file to show, e.g. `motor-data.html`. */
  addInUrl: string;
}

/**
 * Asks the launcher to show another add-in page.
 *
 * @param addInUrl - The add-in HTML file to show.
 */
export function dispatchInStructureNavigation(addInUrl: string) {
  window.dispatchEvent(
    new CustomEvent<NavigateAddInDetail>(NAVIGATE_ADDIN_EVENT, { detail: { addInUrl } }),
  );
}

/**
 * The add-in pages this build contains, by file name.
 *
 * Asked of the build itself rather than of the page list: the page list pulls in every page
 * component, and a page component asking it back would close a circle. Each add-in page is an
 * HTML entry point next to this source tree, so their presence is the question, and Vite answers
 * it while building — nothing is loaded at run time.
 */
const ADD_IN_PAGES = new Set(
  Object.keys(import.meta.glob("/*.html")).map(path => path.replace(/^\//, "")),
);

/**
 * Whether a page can be jumped to at all.
 *
 * Pages arrive one at a time, so a jump can name a page this build does not have yet. Offering
 * it would be a button that does nothing inside the launcher and opens a missing page outside
 * it.
 *
 * @param addInUrl - The add-in HTML file, e.g. `route.html`.
 */
export function isAddInPageAvailable(addInUrl: string): boolean {
  return ADD_IN_PAGES.has(addInUrl);
}

/**
 * Whether this page is running inside the launcher rather than as a page of its own.
 *
 * Only inside it can a jump be handled, so a caller has to be able to fall back to opening the
 * page directly.
 */
export function isInHomeStructure() {
  const pathname = window.location.pathname.toLowerCase();
  return pathname.endsWith("/") || pathname.endsWith("/index.html");
}

/**
 * Leaves a vehicle for another page to pick up when it mounts.
 *
 * @param page - Which page the vehicle is meant for.
 * @param deviceId - The vehicle to preselect.
 */
export function setPendingDeviceId(page: HandoverPage, deviceId: string) {
  sessionStorage.setItem(DEVICE_STORAGE_KEYS[page], deviceId);
}

/**
 * Takes the vehicle left for a page, if there is one.
 *
 * Reading it removes it: it is a handover for one mount, and a leftover id would otherwise
 * override the vehicle the user picks the next time they open the page.
 *
 * @param page - Which page is asking.
 * @returns The vehicle id, or `null` when none was left.
 */
export function consumePendingDeviceId(page: HandoverPage): string | null {
  const key = DEVICE_STORAGE_KEYS[page];
  const value = sessionStorage.getItem(key);
  if (!value) {
    return null;
  }

  sessionStorage.removeItem(key);
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export { NAVIGATE_ADDIN_EVENT };
